import { useState } from 'react'
import { useFlashToast } from '../../hooks/useFlashToast'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFileLines, faCircleCheck, faHourglassHalf,
  faArrowTrendUp, faPlus
} from '@fortawesome/free-solid-svg-icons'
import UserSidebar from '../../components/user/Sidebar'
import UserTopbar from '../../components/user/Topbar'

const COLOR_MAP = {
  green:  { bg: 'bg-green-50',  text: 'text-green-600',  icon: 'bg-green-100' },
  amber:  { bg: 'bg-amber-50',  text: 'text-amber-600',  icon: 'bg-amber-100' },
  blue:   { bg: 'bg-blue-50',   text: 'text-blue-600',   icon: 'bg-blue-100' },
  violet: { bg: 'bg-violet-50', text: 'text-violet-600', icon: 'bg-violet-100' },
}

const STATUS_LABEL = {
  menunggu: { label: 'Menunggu', cls: 'bg-amber-100 text-amber-700' },
  diproses: { label: 'Diproses', cls: 'bg-blue-100 text-blue-700' },
  selesai:  { label: 'Selesai',  cls: 'bg-green-100 text-green-700' },
  ditolak:  { label: 'Ditolak',  cls: 'bg-red-100 text-red-700' },
}

export default function UserDashboard({ current_user, stats = {}, recent_reports = [] }) {
  const STATS = [
    { label: 'Total Laporan Saya', value: stats.total    ?? 0, color: 'green',  icon: faFileLines },
    { label: 'Menunggu',           value: stats.menunggu ?? 0, color: 'amber',  icon: faHourglassHalf },
    { label: 'Sedang Diproses',    value: stats.diproses ?? 0, color: 'blue',   icon: faArrowTrendUp },
    { label: 'Selesai',            value: stats.selesai  ?? 0, color: 'violet', icon: faCircleCheck },
  ]
  useFlashToast()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  return (
    <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
      <UserSidebar current_user={current_user} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <UserTopbar
          current_user={current_user}
          onMenuToggle={() => setSidebarOpen(true)}
          placeholder="Cari laporan saya..."
        />

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">

          <div className="mb-6">
            <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Selamat datang, <span className="font-medium text-gray-700">{current_user?.name}</span>
            </p>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
            {STATS.map(stat => {
              const c = COLOR_MAP[stat.color]
              return (
                <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4">
                  <div className={`w-11 h-11 ${c.icon} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <FontAwesomeIcon icon={stat.icon} className={`w-5 h-5 ${c.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-2xl font-bold ${c.text}`}>{stat.value}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{stat.label}</div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Quick action */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <h2 className="text-sm font-semibold text-gray-700 mb-4">Aksi Cepat</h2>
            <div className="flex flex-wrap gap-3">
              <a href="/user/reports/new" className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors">
                <FontAwesomeIcon icon={faPlus} className="w-3.5 h-3.5" />
                Buat Laporan Baru
              </a>
              <a href="/user/reports" className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors">
                Laporan Saya
              </a>
            </div>
          </div>

          {/* Recent reports */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-800">Laporan Terbaru Saya</h2>
              <a href="/user/reports" className="text-xs text-green-600 hover:text-green-700 font-medium">Lihat Semua →</a>
            </div>
            {recent_reports.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                  <FontAwesomeIcon icon={faFileLines} className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-500">Belum ada laporan</p>
                <p className="text-xs text-gray-400 mt-1">Laporan yang Anda buat akan muncul di sini</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {recent_reports.map(r => {
                  const s = STATUS_LABEL[r.status] || { label: r.status, cls: 'bg-gray-100 text-gray-600' }
                  return (
                    <li key={r.id}>
                      <a href={`/user/reports/${r.id}`} className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{r.title}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{r.category} · {r.created_at}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${s.cls}`}>{s.label}</span>
                          <span className="text-xs text-gray-400 font-mono">{r.tracking_code}</span>
                        </div>
                      </a>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

        </main>
      </div>
    </div>
  )
}
